/**
 * RDV en ligne (#429) — Story "quantité par prestation" (#711). Shared
 * `{serviceDescriptionId, quantity}[]` line shape used by every payload that
 * carries a service basket (proposals, public rdv-request submission, manager
 * accept), plus the ONE duration-summing helper that replaces the three
 * divergent per-site calculations that used to exist (planning engine snapshot,
 * public-submission snapshot, accept's derived Event window).
 *
 * ## Representation
 * A dedicated `quantity` field per line — never id repetition. A client who
 * wants "Parage physiologique × 2" sends ONE line
 * `{serviceDescriptionId: "svc-1", quantity: 2}`, not the id twice. Ids stay
 * unique within a basket (enforced by `buildServiceLinesSchema`'s refine).
 *
 * `services` is the ONLY accepted shape (#724 — the transient
 * `serviceDescriptionIds: string[]` retro-compat from #711 was dropped once the
 * FE migration (PR-B, #728) fully rolled out to prod). A payload still sending
 * only the legacy shape now 400s: with `services` absent, Zod's base type
 * check on the array schema fails first ("Required" / invalid_type) — the
 * `.min(1)` bound below never runs for this case. That bound stays needed for
 * a DIFFERENT case: an explicit `services: []` (a real empty basket), which
 * passes the array-type check and must still be rejected.
 */
import { z } from "zod";
import { PUBLIC_API_SHORT_STRING_MAX } from "../publicApi/bounds.js";
/** Quantity bounds (#711) — a client books at most a handful of the same prestation. */
export const RDV_SERVICE_QUANTITY_MIN = 1;
export const RDV_SERVICE_QUANTITY_MAX = 10;
const RdvServiceLineSchema = z.object({
    serviceDescriptionId: z.string().min(1).max(PUBLIC_API_SHORT_STRING_MAX),
    quantity: z.number().int().min(RDV_SERVICE_QUANTITY_MIN).max(RDV_SERVICE_QUANTITY_MAX),
});
/**
 * The `services` array itself: at least one line, ids unique within the
 * basket, optionally capped at `maxLines` (anti-abuse, public payloads only).
 * The uniqueness refine lives on the array schema (not on the enclosing
 * object) so it only ever runs once each line has ALREADY validated
 * individually — no "dirty object, raw value" edge case to guard against.
 */
function serviceLinesArraySchema(maxLines) {
    let schema = z.array(RdvServiceLineSchema).min(1, "At least one service is required");
    if (maxLines != null) {
        schema = schema.max(maxLines, "Too many services");
    }
    return schema.refine((services) => new Set(services.map((line) => line.serviceDescriptionId)).size === services.length, { message: "Service description ids must be unique" });
}
/**
 * Builds a Zod schema that merges `otherShape` (the payload's other fields)
 * with a required `services: {serviceDescriptionId, quantity}[]` basket.
 *
 * `maxLines` bounds the basket size (anti-abuse, public payloads only); omit it
 * for an internal/manager payload with no such cap (mirrors the pre-#711
 * `AcceptRdvRequestPayloadSchema`, which never bounded its ids array).
 */
export function buildServiceLinesSchema(otherShape, options = {}) {
    return z.object({
        ...otherShape,
        services: serviceLinesArraySchema(options.maxLines),
    });
}
/**
 * Normalizes one snapshotted service-basket line (#760): a `RdvRequestServiceLine`
 * stored before #711 has no `quantity` key in its JSON despite the TS type
 * declaring it required — a demande with no recorded quantity was, by
 * construction, a single unit. Single source of the `?? 1` fallback, previously
 * duplicated across 5 read sites (`rdvRequestDetail.service.ts` ×2,
 * `rdvRequestList.service.ts`, `rdvRequestNotification.service.ts`,
 * `rdvAccept.service.ts`).
 */
export function normalizeSnapshotServiceLine(line) {
    return {
        serviceDescriptionId: line.serviceDescriptionId,
        label: line.label,
        durationMinutes: line.durationMinutes,
        quantity: line.quantity ?? 1,
    };
}
/**
 * Total duration in minutes of a service basket — Σ(unit duration × quantity).
 * Pure and generic over any `{durationMinutes, quantity}` line, so it is the
 * SINGLE calculation shared by the three backend sites that used to diverge
 * (#711): the planning engine's slot sizing, the public submission's frozen
 * snapshot, and the accept flow's derived Event window.
 */
export function sumServiceLineDurations(lines) {
    return lines.reduce((total, line) => total + line.durationMinutes * line.quantity, 0);
}
//# sourceMappingURL=serviceLines.js.map